import { DEFAULT_DESIGN_AREA_IDENTIFIER } from './buildDesignData';
import { formatHexColor } from './editorUnits';
import { replaceSvgColorAreas } from './svgColorReplace';
import {
  buildDisplaySvg,
  extractSvgInnerContent,
  getTrimViewBoxFromItem,
  resolveTextDisplayPathData,
} from './svgDocument';
import type { EditorItem, EditorState, ImageItem, TextItem } from '../Reducers/Configurator/types';

export type BuildDesignSvgContext = {
  canvasWidthMm: number;
  canvasHeightMm: number;
};

function escapeXmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    .replace(/</g, '&lt;');
}

function buildCanvasSvgMarkup(canvasWidthMm: number, canvasHeightMm: number, innerMarkup: string): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
    `viewBox="0 0 ${canvasWidthMm} ${canvasHeightMm}" ` +
    `width="${canvasWidthMm}" height="${canvasHeightMm}">` +
    innerMarkup +
    `</svg>`
  );
}

function buildTextItemMarkup(item: TextItem): string {
  if (!item.pathData) return '';

  const renderScale = item.width / item.viewBoxWidth;
  const pathData = resolveTextDisplayPathData(item);
  const color = formatHexColor(item.color);

  return (
    `<g transform="translate(${item.x},${item.y}) scale(${renderScale})">` +
    `<path d="${escapeXmlAttr(pathData)}" fill="${color}"/>` +
    `</g>`
  );
}

function buildImageItemMarkup(item: ImageItem): string {
  const trimX = item.trimViewBoxX ?? 0;
  const trimY = item.trimViewBoxY ?? 0;
  const { viewBoxWidth, viewBoxHeight } = item;

  let inner = '';
  if (item.svgSource?.trim()) {
    const displaySvg = buildDisplaySvg(item.svgSource, getTrimViewBoxFromItem(item));
    const colored = item.colorAreas?.length
      ? replaceSvgColorAreas(displaySvg, item.colorAreas)
      : displaySvg;
    inner = extractSvgInnerContent(colored);
  } else if (item.url) {
    inner =
      `<image xlink:href="${escapeXmlAttr(item.url)}" x="0" y="0" ` +
      `width="${viewBoxWidth}" height="${viewBoxHeight}" preserveAspectRatio="xMidYMid meet"/>`;
  }

  return (
    `<svg x="${item.x}" y="${item.y}" width="${item.width}" height="${item.height}" ` +
    `viewBox="${trimX} ${trimY} ${viewBoxWidth} ${viewBoxHeight}" ` +
    `preserveAspectRatio="xMidYMid meet" overflow="visible">` +
    inner +
    `</svg>`
  );
}

function buildItemMarkup(item: EditorItem): string {
  if (item.type === 'text') {
    return buildTextItemMarkup(item);
  }
  return buildImageItemMarkup(item);
}

/** Match canvas visual stacking: images below text. */
function sortItemsForPrint(items: EditorItem[]): EditorItem[] {
  return [...items].sort((a, b) => {
    const rank = (item: EditorItem) => (item.type === 'text' ? 1 : 0);
    return rank(a) - rank(b);
  });
}

/** Per-element SVG exports keyed by design area (core `getScreenshotsOfElements` shape). */
export function buildEditorElementScreenshots(
  editor: EditorState,
  context: BuildDesignSvgContext
): Record<string, string[]> {
  const items = Object.values(editor.objects);
  if (items.length === 0) return {};

  const { canvasWidthMm, canvasHeightMm } = context;

  return {
    [DEFAULT_DESIGN_AREA_IDENTIFIER]: items.map((item) =>
      buildCanvasSvgMarkup(canvasWidthMm, canvasHeightMm, buildItemMarkup(item))
    ),
  };
}

/** Full-canvas SVG exports keyed by design area (core `getScreenshot` shape). */
export function buildEditorCanvasScreenshots(
  editor: EditorState,
  context: BuildDesignSvgContext
): Record<string, string> {
  const items = Object.values(editor.objects);
  if (items.length === 0) return {};

  const { canvasWidthMm, canvasHeightMm } = context;
  const innerMarkup = sortItemsForPrint(items)
    .map((item) => buildItemMarkup(item))
    .join('');

  return {
    [DEFAULT_DESIGN_AREA_IDENTIFIER]: buildCanvasSvgMarkup(canvasWidthMm, canvasHeightMm, innerMarkup),
  };
}
